import {useEffect, useMemo, useRef} from 'react';

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const SCROLL_RETRY_MS = [0, 100, 250, 500, 900, 1500, 2500, 4000];

/** Collapse whitespace and normalize report DOM text so it matches coverage excerpts. */
export function normalizeForMatch(text) {
    let normalized = (text || '')
        .replace(/[\u201c\u201d\u2018\u2019]/g, '"')
        .replace(/[\u2013\u2014\u2212]/g, '-')
        .replace(/\u00a0/g, ' ')
        .replace(/\[\d+(?:,\d+)*\]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    // Strip leading block classification prefixes rendered as "(U) " or "(U//FOUO) ".
    normalized = normalized.replace(/^(\([a-z0-9/]+\)\s*)+/i, '');
    return normalized;
}

function safeDecodeQuery(q) {
    try {
        return decodeURIComponent(q);
    } catch {
        return q;
    }
}

function appendWordNgramTerms(terms, text) {
    const words = normalizeForMatch(text).split(' ').filter(w => w.length >= 3);
    if (words.length < 4) {
        return;
    }
    const seen = new Set(terms.map(t => normalizeForMatch(t)));
    for (let len = Math.min(8, words.length); len >= 4; len -= 1) {
        for (let i = 0; i <= words.length - len; i += 1) {
            const phrase = words.slice(i, i + len).join(' ');
            if (phrase.length >= 20 && !seen.has(phrase)) {
                terms.push(phrase);
                seen.add(phrase);
            }
        }
    }
}

/**
 * Build progressively shorter phrase candidates from a search query
 * (never split to single words to avoid matching titles).
 */
export function buildExcerptSearchTerms(q) {
    if (!q) {
        return [];
    }
    const full = safeDecodeQuery(q).trim();
    if (!full) {
        return [];
    }
    const terms = [full];
    let slice = full;
    while (slice.length > 20) {
        slice = slice.slice(0, Math.ceil(slice.length * 0.6));
        if (slice.length >= 15) {
            terms.push(slice);
        }
    }
    appendWordNgramTerms(terms, full);
    return terms;
}

/**
 * Extract contiguous windows of `text` around each occurrence of `q`, trimmed to word
 * boundaries. Stopwords are preserved (unlike {@link buildExcerptSearchTerms}'s ngram
 * builder), so the resulting phrase appears verbatim in the rendered report DOM and lands
 * the scroll directly on the query match.
 */
function buildQueryContextWindows(text, q) {
    if (!q) {
        return [];
    }
    const windows = [];
    const lower = text.toLowerCase();
    let from = 0;
    let idx = lower.indexOf(q, from);
    while (idx !== -1 && windows.length < 6) {
        const start = Math.max(0, idx - 40);
        const end = Math.min(text.length, idx + q.length + 40);
        let phrase = text.slice(start, end);
        // Drop partial words clipped at each edge so the phrase matches DOM text cleanly.
        if (start > 0) {
            phrase = phrase.replace(/^\S+\s+/, '');
        }
        if (end < text.length) {
            phrase = phrase.replace(/\s+\S+$/, '');
        }
        phrase = phrase.trim();
        if (phrase.length >= 15) {
            windows.push(phrase);
        }
        from = idx + q.length;
        idx = lower.indexOf(q, from);
    }
    return windows;
}

/**
 * Build excerpt-scroll terms for a report SEARCH HIT.
 *
 * The hit's `snippet` is a large sliding-window chunk (multiple sentences/paragraphs, and
 * may begin mid-sentence from a prior-chunk overlap). {@link buildExcerptSearchTerms} is
 * tuned for a short highlighted phrase, and its ngram builder drops stopwords — so the
 * phrases it derives from prose no longer appear verbatim in the rendered DOM and fail to
 * match. Instead we build contiguous, stopword-preserving candidates and prioritise those
 * containing the user's `query` so the viewer scrolls to the most relevant matched passage
 * rather than the chunk boundary.
 *
 * Order: query-context windows (longest first) → sentences containing the query (longest
 * first) → remaining sentences (longest first). {@link findExcerptScrollTarget} returns the
 * smallest DOM leaf for the first term that matches.
 */
export function buildSnippetScrollTerms(snippet, query) {
    const text = (snippet || '').replace(/\s+/g, ' ').trim();
    if (!text) {
        return [];
    }

    const q = (query || '').replace(/\s+/g, ' ').trim().toLowerCase();

    // Sentence-ish fragments (keep the terminator); avoids JS lookbehind for Safari support.
    const rawFragments = text.match(/[^.!?;:]+[.!?;:]?/g) || [];
    const sentencesWithQuery = [];
    const sentencesWithoutQuery = [];
    const seen = new Set();
    for (const raw of rawFragments) {
        const frag = raw.trim();
        if (frag.length < 25) {
            continue;
        }
        const key = frag.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        if (q && key.includes(q)) {
            sentencesWithQuery.push(frag);
        } else {
            sentencesWithoutQuery.push(frag);
        }
    }

    if (sentencesWithQuery.length === 0 && sentencesWithoutQuery.length === 0 && text.length >= 20) {
        sentencesWithoutQuery.push(text);
    }

    const byLenDesc = (a, b) => b.length - a.length;
    sentencesWithQuery.sort(byLenDesc);
    sentencesWithoutQuery.sort(byLenDesc);

    const queryWindows = buildQueryContextWindows(text, q).sort(byLenDesc);

    // De-duplicate across buckets while preserving priority order.
    const ordered = [];
    const added = new Set();
    for (const term of [...queryWindows, ...sentencesWithQuery, ...sentencesWithoutQuery]) {
        const key = term.toLowerCase();
        if (!added.has(key)) {
            added.add(key);
            ordered.push(term);
        }
    }
    return ordered;
}

/** Block tags that are valid excerpt highlight targets (never structural wrappers). */
const PREFERRED_BLOCK_TAGS = new Set(['P', 'LI', 'TD', 'TH', 'BLOCKQUOTE', 'PRE', 'FIGCAPTION']);

function isExcludedScrollTarget(el) {
    if (!el) {
        return false;
    }
    if (el.hasAttribute?.('data-report-scroll-skip')) {
        return true;
    }
    if (HEADING_TAGS.has(el.tagName)) {
        return true;
    }
    return Boolean(el.closest('[data-report-scroll-skip], h1'));
}

function isPreferredBlock(el) {
    return Boolean(el && PREFERRED_BLOCK_TAGS.has(el.tagName));
}

/** True when an element is a multi-block container (e.g. body Stack wrapping several <p>s). */
function isMultiBlockWrapper(el) {
    if (!el?.children?.length) {
        return false;
    }
    let blockKids = 0;
    for (const child of el.children) {
        if (isPreferredBlock(child) || child.children?.length > 0) {
            blockKids += 1;
            if (blockKids >= 2) {
                return true;
            }
        }
    }
    return false;
}

function collectMatchingLeaves(el, needleNorm, root, skipHeadings, out) {
    if (isExcludedScrollTarget(el)) {
        return;
    }

    const elNorm = normalizeForMatch(el.textContent);
    if (!elNorm.includes(needleNorm)) {
        return;
    }

    let matchingChild = false;
    for (const child of el.children) {
        if (skipHeadings && HEADING_TAGS.has(child.tagName)) {
            continue;
        }
        if (isExcludedScrollTarget(child)) {
            continue;
        }
        const sizeBefore = out.length;
        collectMatchingLeaves(child, needleNorm, root, skipHeadings, out);
        if (out.length > sizeBefore) {
            matchingChild = true;
        }
    }

    if (!matchingChild && el !== root) {
        out.push(el);
    }
}

function pickSmallest(candidates) {
    if (!candidates.length) {
        return null;
    }
    return candidates.slice().sort((a, b) => a.textContent.length - b.textContent.length)[0];
}

/**
 * Prefer the smallest DOM node that still contains the phrase (avoids scrolling to doc top).
 *
 * When the report marks a body region with {@code data-report-excerpt-body}, search only
 * inside it so title/header/evaluation chrome never receive the yellow excerpt highlight.
 * Long cross-paragraph snippet windows that only match a multi-block wrapper are skipped in
 * favor of shorter terms that land on a single preferred block (`p`, `li`, …).
 */
export function findExcerptScrollTarget(root, searchTerms, {skipHeadings = true} = {}) {
    if (!root || !searchTerms?.length) {
        return null;
    }

    const searchRoot = (typeof root.querySelector === 'function'
        && root.querySelector('[data-report-excerpt-body]'))
        || root;

    let fallback = null;
    for (const term of searchTerms) {
        const needleNorm = normalizeForMatch(term);
        if (needleNorm.length < 8) {
            continue;
        }
        const candidates = [];
        collectMatchingLeaves(searchRoot, needleNorm, searchRoot, skipHeadings, candidates);
        const filtered = candidates.filter(c => !isExcludedScrollTarget(c));
        if (!filtered.length) {
            continue;
        }
        const preferred = filtered.filter(isPreferredBlock);
        if (preferred.length) {
            return pickSmallest(preferred);
        }
        const nonWrappers = filtered.filter(c => !isMultiBlockWrapper(c));
        const pick = pickSmallest(nonWrappers.length ? nonWrappers : filtered);
        if (pick && isPreferredBlock(pick)) {
            return pick;
        }
        if (pick && !fallback) {
            fallback = pick;
        }
        // Keep scanning shorter terms — a later sentence may land on a real <p>.
    }
    return fallback;
}

export function isTargetVisibleInScrollContainer(container, target) {
    if (!target) {
        return false;
    }
    const tRect = target.getBoundingClientRect();
    if (tRect.height <= 0 && tRect.width <= 0) {
        return false;
    }
    if (!container) {
        return tRect.top >= 0 && tRect.bottom <= window.innerHeight;
    }
    const cRect = container.getBoundingClientRect();
    const visibleHeight = Math.min(tRect.bottom, cRect.bottom) - Math.max(tRect.top, cRect.top);
    return visibleHeight >= Math.min(Math.max(tRect.height * 0.2, 24), tRect.height);
}

export function scrollElementIntoContainer(container, element, {block = 'center', behavior = 'auto'} = {}) {
    if (!container || !element) {
        return false;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + container.scrollTop;

    let scrollTop;
    if (block === 'center') {
        scrollTop = elementTop - (container.clientHeight / 2) + (elementRect.height / 2);
    } else {
        scrollTop = elementTop - 24;
    }

    container.scrollTo({
        top: Math.max(0, scrollTop),
        behavior,
    });
    return true;
}

function findScrollableAncestor(el) {
    let node = el.parentElement;
    while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

function scrollTargetIntoView(target, scrollContainer) {
    const container = scrollContainer
        || target.closest('[data-report-scroll-root]')
        || findScrollableAncestor(target);

    if (container && container.scrollHeight > container.clientHeight + 1) {
        scrollElementIntoContainer(container, target, {block: 'center', behavior: 'auto'});
        if (isTargetVisibleInScrollContainer(container, target)) {
            return true;
        }
    }

    if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({behavior: 'auto', block: 'center', inline: 'nearest'});
    }

    if (container) {
        return isTargetVisibleInScrollContainer(container, target);
    }
    return isTargetVisibleInScrollContainer(null, target);
}

/**
 * Marker attribute for the excerpt-highlighted element so we can find and clear a prior highlight
 * (scoped to the report root, so concurrent viewers don't interfere with each other).
 */
const EXCERPT_HIGHLIGHT_ATTR = 'data-report-excerpt-highlight';

/** Remove any prior excerpt highlight under this report root, restoring inline styles. */
export function clearExcerptHighlight(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
        return;
    }
    root.querySelectorAll(`[${EXCERPT_HIGHLIGHT_ATTR}]`).forEach((el) => {
        el.style.backgroundColor = el.dataset.reportExcerptPrevBg || '';
        el.style.borderRadius = el.dataset.reportExcerptPrevRadius || '';
        el.style.boxShadow = '';
        el.style.transition = '';
        delete el.dataset.reportExcerptPrevBg;
        delete el.dataset.reportExcerptPrevRadius;
        el.removeAttribute(EXCERPT_HIGHLIGHT_ATTR);
    });
}

/**
 * Visually mark the matched excerpt element so the user can see which passage the search hit
 * landed on (the viewer otherwise just scrolls, leaving the match indistinguishable from the rest
 * of the document). Non-destructive: only sets inline styles on the target and can be reverted via
 * {@link clearExcerptHighlight}.
 */
export function highlightExcerptTarget(root, target) {
    if (!target) {
        return;
    }
    clearExcerptHighlight(root);
    target.dataset.reportExcerptPrevBg = target.style.backgroundColor || '';
    target.dataset.reportExcerptPrevRadius = target.style.borderRadius || '';
    target.setAttribute(EXCERPT_HIGHLIGHT_ATTR, '');
    target.style.transition = 'background-color 0.4s ease, box-shadow 0.4s ease';
    target.style.backgroundColor = 'rgba(255, 213, 79, 0.4)';
    target.style.boxShadow = '0 0 0 4px rgba(255, 213, 79, 0.28)';
    target.style.borderRadius = '4px';
}

export function scrollToExcerptTarget(root, searchTerms, scrollContainer = null) {
    if (!root) {
        return false;
    }
    const target = findExcerptScrollTarget(root, searchTerms, {skipHeadings: true})
        || findExcerptScrollTarget(root, searchTerms, {skipHeadings: false});
    if (!target) {
        return false;
    }

    highlightExcerptTarget(root, target);
    return scrollTargetIntoView(target, scrollContainer);
}

/**
 * When search terms are present, scroll the report view to the matching excerpt after content
 * renders and highlight the matched passage so the user can see which section the hit landed on.
 * The highlight is applied via inline styles and cleared when the terms change or the hook unmounts.
 */
export function useReportExcerptScroll(contentRef, scrollContainerRef, searchTerms, isLoading, hasContent) {
    const stableTerms = useMemo(() => searchTerms, [JSON.stringify(searchTerms)]);
    const scrolledRef = useRef(false);

    useEffect(() => {
        scrolledRef.current = false;
        clearExcerptHighlight(contentRef.current);
    }, [contentRef, stableTerms, isLoading, hasContent]);

    useEffect(() => {
        if (isLoading || !hasContent || stableTerms.length === 0) {
            return undefined;
        }

        let cancelled = false;
        let observer;
        let debounceTimer;

        const tryScroll = () => {
            if (cancelled || scrolledRef.current) {
                return false;
            }
            const root = contentRef.current;
            if (!root) {
                return false;
            }
            const container = scrollContainerRef?.current || null;
            const ok = scrollToExcerptTarget(root, stableTerms, container);
            if (ok) {
                scrolledRef.current = true;
                observer?.disconnect();
            }
            return ok;
        };

        const scheduleTry = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(tryScroll, 50);
        };

        const timers = SCROLL_RETRY_MS.map(delay => setTimeout(tryScroll, delay));

        const root = contentRef.current;
        if (root && typeof MutationObserver !== 'undefined') {
            observer = new MutationObserver(scheduleTry);
            observer.observe(root, {childList: true, subtree: true, characterData: true});
        }

        return () => {
            cancelled = true;
            observer?.disconnect();
            clearTimeout(debounceTimer);
            timers.forEach(clearTimeout);
            clearExcerptHighlight(contentRef.current);
        };
    }, [contentRef, scrollContainerRef, stableTerms, isLoading, hasContent]);
}
